/**
 * Value of the RFC 7807 `type` field returned with `401` when the user's authentication is
 * older than the allowed age (the 5-minute consumer secret firewall).
 * Keep in sync with `AuthTimeExceededError.error_message_code` in the Internal API.
 */
export const AUTH_TIME_EXCEEDED_ERROR_TYPE = 'auth_time.exceeded';

/**
 * Custom error class for HTTP errors.
 * Carries the numeric `status` code and `statusText` as first-class properties
 * so consumers can do `error.status === 404` instead of parsing the message string.
 *
 * @example
 * if (error instanceof HttpError && error.status === 404) {
 *   navigate(ROUTES.NOT_FOUND);
 * }
 */
export class HttpError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly detail?: string;
  /**
   * Optional parsed response body for structured error handling.
   * When available, this contains the JSON error payload returned
   * by the backend (RFC 7807: `{ type, title, status, detail }`).
   */
  readonly body?: unknown;

  constructor(status: number, statusText: string, detail?: string, body?: unknown) {
    super(`HTTP Error: ${status} ${statusText}`);
    this.name = 'HttpError';
    this.status = status;
    this.statusText = statusText;
    this.detail = detail;
    this.body = body;
    // Maintains proper stack trace in V8 engines (Chrome, Node)
    Object.setPrototypeOf(this, new.target.prototype);
  }

  /**
   * True for the `401` that means "signed in, but not recently enough" (the consumer secret
   * firewall). It is identified by the RFC 7807 `type`, like every other API error, and
   * needs a fresh login rather than the generic session-expired handling.
   */
  get isAuthTimeExceeded(): boolean {
    const body = this.body as { type?: unknown } | null | undefined;
    return this.status === 401 && body?.type === AUTH_TIME_EXCEEDED_ERROR_TYPE;
  }
}

/** True when `error` is the consumer-secret firewall 401 (`type: auth_time.exceeded`). */
export function isAuthTimeExceededError(error: unknown): error is HttpError {
  return error instanceof HttpError && error.isAuthTimeExceeded;
}
